/* eslint-disable no-empty */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo, useState, useEffect, useRef } from "react";
import Select from "react-select";
import { IMaskInput } from "react-imask";
import { FaVideo, FaPhoneAlt, FaCheckCircle } from "react-icons/fa";

import {
  addDays,
  addMinutes,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import moment from "moment-timezone";
import { timezoneFormatter } from "../../../utils/staticData";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
dayjs.extend(utc);
dayjs.extend(timezone);

import axios from "axios";
import MultiStepLeadForm from "./MultiStepLeadForm";
import "./BookAppointment.scss";

const cleintUrlProd = "https://app.unclekam.com";
const cleintUrlStaging = "https://staging.unclekam.com";
// const cleintUrlStaging = "http://localhost:3000";


// Hosts that actually serve /.netlify/functions/api-proxy. Optimizers like
// NitroPack rehost the widget JS on the WordPress origin while keeping the
// original tools host inside the rewritten path, so match a known host
// anywhere in the src rather than trusting the URL's origin.
const TOOLS_HOSTS = ["tools.unclekam.com", "tools-unclekam.netlify.app"];

function toolsOriginFromSrc(src) {
  if (!src) return "";
  try {
    const u = new URL(src, location.href);
    if (TOOLS_HOSTS.includes(u.hostname)) return u.origin;
  } catch {}
  for (const host of TOOLS_HOSTS) {
    if (src.includes(`/${host}/`) || src.includes(`//${host}/`)) {
      return `https://${host}`;
    }
  }
  return "";
}

function detectToolsOrigin() {
  const fromCur = toolsOriginFromSrc(document.currentScript && document.currentScript.src);
  if (fromCur) return fromCur;
  const scripts = Array.from(document.getElementsByTagName("script"));
  for (let i = scripts.length - 1; i >= 0; i--) {
    const fromScan = toolsOriginFromSrc(scripts[i].src || "");
    if (fromScan) return fromScan;
  }
  const isQA = new URLSearchParams(location.search).has("qa_mode");
  return isQA ? "https://tools-unclekam.netlify.app" : "https://tools.unclekam.com";
}

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const ANNUAL_INCOMES = [
  { id: 1, name: "$12,000-$49,999" },
  { id: 2, name: "$50,000-$99,999" },
  { id: 3, name: "$100,000-$249,999" },
  { id: 4, name: "$250,000-$499,999" },
  { id: 5, name: "$500,000+" },
];

// ---- EXACTLY MATCH OLD: 45-minute slots
const SLOT_MINUTES = 45;

/* =========================
   TIMEZONE OPTIONS — match old list/labels and default
   ========================= */

// US top block, then all US zones (like the old ReactSelect)
const buildUSTzOptions = () => {
  const top = ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles"];
  const us = moment.tz.zonesForCountry("US") || [];
  const unique = Array.from(new Set([...top, ...us]));
  return unique.map((tz) => ({ value: tz, label: timezoneFormatter(tz) }));
};

// selected value label should look like: "Eastern Time (12:57 PM)"
const selectedLabel = (tz) =>
  `${timezoneFormatter(tz)} (${dayjs().tz(tz).format("h:mm A")})`;

/* =========================
   SLOT/DATE HELPERS — same pipeline as old file
   (provider local -> UTC -> viewer tz for display)
   ========================= */

const generateTicks = (start, end, stepMinutes = SLOT_MINUTES) => {
  const out = [];
  let cur = new Date(start);
  while (cur < end) {
    out.push(cur);
    cur = addMinutes(cur, stepMinutes);
  }
  return out;
};

const parseTimeHM = (str) => {
  if (!str) return null;
  const s = String(str).trim();

  let m = s.match(/^(\d{1,2}):(\d{2})\s*([ap]m)$/i);
  if (m) {
    let h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    const ap = m[3].toLowerCase();
    if (ap === "pm" && h !== 12) h += 12;
    if (ap === "am" && h === 12) h = 0;
    return { hour: h, minute: min };
  }

  m = s.match(/^(\d{1,2})\s*([ap]m)$/i);
  if (m) {
    let h = parseInt(m[1], 10);
    const ap = m[2].toLowerCase();
    if (ap === "pm" && h !== 12) h += 12;
    if (ap === "am" && h === 12) h = 0;
    return { hour: h, minute: 0 };
  }

  m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (m) return { hour: parseInt(m[1], 10), minute: parseInt(m[2], 10) };

  m = s.match(/^(\d{1,2})$/);
  if (m) return { hour: parseInt(m[1], 10), minute: 0 };

  return null;
};

// Interpret pro's availability as LOCAL(proTZ), then produce UTC Date
const dateAtLocalTime = (jsDate, timeStr, providerTZ) => {
  if (!jsDate || !timeStr || !providerTZ) return null;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: providerTZ });
  } catch {
    providerTZ = "UTC";
  }

  const parts = parseTimeHM(timeStr);
  if (!parts) return null;

  const y = jsDate.getFullYear();
  const m = jsDate.getMonth() + 1;
  const d = jsDate.getDate();

  const baseLocal = dayjs.tz(
    `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}T00:00`,
    providerTZ
  );
  if (!baseLocal.isValid()) return null;

  const dt = baseLocal.add(parts.hour, "hour").add(parts.minute, "minute");
  return dt.isValid() ? dt.utc().toDate() : null;
};

const buildSlotsForDate = (date, payload, selectedTZ) => {
  const providerTZ = payload?.settings?.timezone;
  const weekdayName = WEEKDAYS[date.getDay()];
  const dayRanges = payload?.availability?.time_ranges?.[weekdayName];
  if (!providerTZ || !Array.isArray(dayRanges) || dayRanges.length === 0) return [];

  const slots = [];
  for (const range of dayRanges) {
    if (!range?.from || !range?.to) continue;

    const startUtc = dateAtLocalTime(date, range.from, providerTZ);
    const endUtc = dateAtLocalTime(date, range.to, providerTZ);
    if (!startUtc || !endUtc || !(endUtc > startUtc)) continue;

    const ticks = generateTicks(startUtc, endUtc, SLOT_MINUTES);
    for (const utcDate of ticks) {
      const label = dayjs(utcDate).utc().tz(selectedTZ).format("h:mm A"); // no leading 0 — matches old
      slots.push({ utc: utcDate, iso: utcDate.toISOString(), label });
    }
  }
  return slots;
};

const WeekHeader = () => (
  <div className="row row-cols-7 g-2 text-center text-muted small mb-1">
    {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
      <div className="col" key={d}><div className="py-1">{d}</div></div>
    ))}
  </div>
);

const MonthGrid = ({
  monthCursor,
  selectedDate,
  onSelectDate,
  onPrevMonth,
  onNextMonth,
  isDayAvailable,
}) => {
  const startMonth = startOfMonth(monthCursor);
  const endMonth_  = endOfMonth(monthCursor);
  const gridStart  = startOfWeek(startMonth, { weekStartsOn: 0 });
  const gridEnd    = endOfWeek(endMonth_,   { weekStartsOn: 0 });
  

  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  const weeks = [];
  let d = gridStart;
  while (d <= gridEnd) {
    const oneWeek = [];
    for (let i = 0; i < 7; i++) {
      const cellDate = d;
      const inMonth    = isSameMonth(cellDate, monthCursor);
      const isSelected = selectedDate && isSameDay(cellDate, selectedDate);
      const isToday    = isSameDay(cellDate, today);

      const cellStart  = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate());
      const pastOrToday = cellStart <= todayStart; // disable today + past
      const available   = inMonth && isDayAvailable(cellDate) && !pastOrToday;

      oneWeek.push(
        <div className="col" key={cellDate.toISOString()}>
          <div className="ratio ratio-1x1">
            <button
              type="button"
              className={[
                "cal-day w-100 h-100",
                !inMonth ? "cal-day--out" : "",
                available ? "cal-day--available" : "cal-day--disabled",
                isSelected ? "cal-day--selected" : "",
                isToday ? "cal-day--today" : "",
              ].join(" ").trim()}
              onClick={() => available && onSelectDate(cellDate)}
              disabled={!available}
            >
              {format(cellDate, "d")}
            </button>
          </div>
        </div>
      );
      d = addDays(d, 1);
    }
    weeks.push(oneWeek);
  }

  return (
    <div className="calendar">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <button type="button" className="nav-btn" onClick={onPrevMonth} aria-label="Previous month">‹</button>
        <div className="fw-semibold">{format(monthCursor, "MMMM yyyy")}</div>
        <button type="button" className="nav-btn" onClick={onNextMonth} aria-label="Next month">›</button>
      </div>

      <WeekHeader />

      {weeks.map((weekNodes, idx) => (
        <div className="row g-2 mb-2" key={idx}>
          {weekNodes}
        </div>
      ))}
    </div>
  );
};

const BookAppointmentFrom = ({ qa_mode, auto_login = true, proxyUrl}) => {
  const tzOptions = useMemo(buildUSTzOptions, []);
  const PROXY_URL = proxyUrl || window.__UK_PROXY_URL__ ||`${detectToolsOrigin()}/.netlify/functions/api-proxy`; ;

  const initialTz = useMemo(() => {
    const guess = moment.tz.guess();
    return guess && guess.startsWith("America/") ? guess : "America/Adak";
  }, []);
  const [selectedTZ, setSelectedTZ] = useState(() => {
    const opt = tzOptions.find((o) => o.value === initialTz) || { value: initialTz, label: timezoneFormatter(initialTz) };
    return opt;
  });
  const zipRef = useRef(null);

  const [monthCursor, setMonthCursor] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(addDays(new Date(), 1));
  const [selectedSlotIso, setSelectedSlotIso] = useState(null);
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);

  const [formData, setFormData] = useState(() => ({ options: [], income: "", zip: "" }));
  const [taxProAvailability, setTaxProAvailability] = useState(null);

  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const email = params.get("email");
  const pro_id = params.get("pro_id");


  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(false);
  const [appointmentType, setAppointmentType] = useState("video");
  const [userCheck, setUserCheck] = useState({});
  const [createUserSteps, setCreateUserSteps] = useState(1);
  const [createLeadSteps, setCreateLeadSteps] = useState(1);

  const qa = useMemo(() => (typeof qa_mode === "boolean" ? qa_mode : params.has("qa_mode")), [qa_mode, params]);
  const CLIENT_URL = qa ? cleintUrlStaging : cleintUrlProd;

  // identical signature/behavior to old util
  function convertTimeIntoSselectedTZ(timezoneAssign, targetTimeZone, date, formatStr) {
    const currentTimeWithTZ = moment.tz(moment(date).format("YYYY-MM-DDTHH:mm:ss"), timezoneAssign);
    return moment(currentTimeWithTZ).tz(targetTimeZone).format(formatStr || "YYYY-MM-DD HH:mm:ss");
  }
  const getUserTZ = (u, fallback = "UTC") => u?.settings?.timezone || fallback;

  const createAppointment = async (lead,isMultyStep) => {
    setLoading(true);
    try {
      if (!selectedSlotIso) {
        setStatus("error");
        setMessage("Pick a time first.");
        return;
      }
      if (!lead) {
        setStatus("error");
        setMessage("Lead not loaded yet.");
        return;
      }

      const duration = 45;
      const startUtcIso = selectedSlotIso;
      const endUtcIso = dayjs(startUtcIso).add(duration, "minute").toISOString();

      const proTZ =
        getUserTZ(lead?.TaxPro?.user) ||
        getUserTZ(lead?.taxPro?.user) ||
        getUserTZ(taxProAvailability) ||
        "UTC";

      const startDateLocal = convertTimeIntoSselectedTZ("UTC", proTZ, startUtcIso, "YYYY-MM-DD HH:mm:ss");
      const endDateLocal = convertTimeIntoSselectedTZ("UTC", proTZ, endUtcIso, "YYYY-MM-DD HH:mm:ss");

      const appointmentObj = { timezone: proTZ, startDate: startDateLocal, endDate: endDateLocal, duration };

    if (appointmentType === "video") {
      const meetingData = {
        taxProEmail: lead?.taxPro?.user?.email || lead?.user?.email,
        email: lead?.user?.email,
        startTime: moment(selectedSlotIso).tz(proTZ).format("YYYY-MM-DD HH:mm:ss"),
        duration,
        timezone: proTZ,
      };

      await axios.post(
        PROXY_URL,
        {
          path: "/createZoomMeeting",
          method: "PUT",
          qa_mode: qa,
          data: meetingData,
        },
        { headers: { "Content-Type": "application/json" } }
      ).then((response) => { 
        appointmentObj.roomId = response?.data?.data?.meetingID || null;
      });

    }

   await axios.post(
     PROXY_URL,
     {
       path: `/booking/${lead?.uuid || lead?.lead?.uuid}/${
         lead?.user?.uuid
       }/${lead?.taxPro?.user?.uuid || lead?.userpro?.user?.uuid}`,
       method: "POST",
       qa_mode: qa,
       data: appointmentObj,
     },
     { headers: { "Content-Type": "application/json" } }
   );

      setLoading(false);
      if (!isMultyStep) {
        if (auto_login) {
          window.location.href = `${CLIENT_URL}/login-with-token/${userCheck?.user?.verification_token}?email=${userCheck?.user?.email}`;
        } else {
          window.location.href = `${CLIENT_URL}/dashboard`;
        }
      }

      setStatus("success");
      setMessage("Appointment created ✅");
    } catch (error) {
      setLoading(false);
      console.log(error);
      setStatus("error");
      setMessage(error?.response?.data?.message || error?.message || "Failed to book appointment.");
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      setServicesLoading(true);
      try {
    const { data } = await axios.post(
      PROXY_URL,
      {
        path: "/get-services",
        method: "GET",
        qa_mode: qa,
      },
      { headers: { "Content-Type": "application/json" } }
    );
        if (active) setServices(Array.isArray(data) ? data : []);
      } catch {
        if (active) setServices([]);
      } finally {
        if (active) setServicesLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [PROXY_URL, qa]);

  const getAvailableTaxPro = async () => {
    try {
     const resp = await axios.post(
        PROXY_URL,
       {
         path: "/get-available-taxpro",
         method: "POST",
         qa_mode: qa,
         data: { pro_id },
       },
       { headers: { "Content-Type": "application/json" } }
     );

      setTaxProAvailability({ ...resp?.data?.data?.pro?.user, pro_id: resp?.data?.data?.pro?.id, pro_uuid: resp?.data?.data?.pro?.uuid });
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (Object.keys(userCheck)?.length > 0 && !userCheck?.taxProAssigned){
      getAvailableTaxPro()
    };
  }, [userCheck]);

  const getAvailability = async () => {
    try {
      setStatus("loading");
     const resp = await axios.post(
        PROXY_URL,
       {
         path: "/getTaxProAvailabilityByEmail",
         method: "POST",
         qa_mode: qa,
         data: { email },
       },
       { headers: { "Content-Type": "application/json" } }
     );

      const maybe =
        resp?.data?.data?.taxPro?.user || resp?.data?.data?.taxPro || resp?.data?.data || resp?.data;

      setUserCheck(resp?.data?.data);
      setLead(resp?.data?.data);
      if (resp?.data?.data?.userExist && resp?.data?.data?.leadExists) {
        setTaxProAvailability(maybe || null);
      }
      setStatus("success");
    } catch (err) {
      console.log("BookAppointmentFrom error:", err);
      setStatus("error");
      setMessage(err?.response?.data?.message || err?.message || "Request failed.");
      setTaxProAvailability(null);
    }
  };

  useEffect(() => {
    if (!email) {
      setUserCheck({ userExist: false, leadExists: false });
      setStatus("idle");
      getAvailableTaxPro();
      return;
    }
    getAvailability();
  }, [email, qa, pro_id]);

  const hasPayload =
    !!taxProAvailability &&
    !!taxProAvailability.settings?.timezone &&
    !!taxProAvailability.availability?.time_ranges &&
    typeof taxProAvailability.availability.time_ranges === "object";

  useEffect(() => {
    if (!hasPayload) return;
    const today = new Date();
    const tomorrow = addDays(today, 1);
    const selectedIsPastOrToday =
      new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate()) <=
      new Date(today.getFullYear(), today.getMonth(), today.getDate());

    if (selectedIsPastOrToday) {
      let found = null;
      for (let i = 0; i < 90; i++) {
        const d = addDays(tomorrow, i);
        const s = buildSlotsForDate(d, taxProAvailability, selectedTZ.value);
        if (s.length > 0) {
          found = d;
          break;
        }
      }
      setSelectedDate(found || tomorrow);
      setSelectedSlotIso(null);
    }
  }, [hasPayload, taxProAvailability, selectedTZ, monthCursor]);


   useEffect(() => {
   if (!hasPayload) return;
   const today = new Date();
   let firstAvailable = null;
   for (let i = 1; i <= 90; i++) {
     const d = addDays(today, i);
     const s = buildSlotsForDate(d, taxProAvailability, selectedTZ.value);
     if (s.length > 0) { firstAvailable = d; break; }
   }
   if (firstAvailable && !isSameMonth(firstAvailable, monthCursor)) {
     setMonthCursor(startOfMonth(firstAvailable));
   }
 }, [hasPayload, selectedTZ, taxProAvailability, monthCursor]);

  const next3Days = useMemo(() => {
    if (!hasPayload) return { list: [], set: new Set() };

    const today = new Date();
    const out = [];
    for (let i = 1; i <= 120 && out.length < 5; i++) {
      const d = addDays(today, i);
      const slots = buildSlotsForDate(d, taxProAvailability, selectedTZ.value);
      if (slots.length > 0) {
        out.push(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
      }
    }
    return { list: out, set: new Set(out) };
  }, [hasPayload, taxProAvailability, selectedTZ]);

  const isDayAvailable = (d) => {
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    return next3Days.set.has(key);
  };

  const slots = useMemo(() => {
    if (!hasPayload) return [];
    return buildSlotsForDate(selectedDate, taxProAvailability, selectedTZ.value);
  }, [selectedDate, selectedTZ, taxProAvailability, hasPayload]);

  const prevMonth = () => setMonthCursor((cur) => addMonths(cur, -1));
  const nextMonth = () => setMonthCursor((cur) => addMonths(cur, 1));
  const pickDate = (d) => {
    setSelectedDate(d);
    setSelectedSlotIso(null);
  };

  const AppointmentTypePicker = ({ appointmentType, setAppointmentType }) => (
    <div className="opt-grid mt-5 ">
      <label className={`opt-card ${appointmentType === "video" ? "active" : ""}`}>
        <input
          type="radio"
          name="appointment-type"
          value="video"
          checked={appointmentType === "video"}
          onChange={() => setAppointmentType("video")}
        />
        <div className="opt-left">
          <div className="opt-icon-wrap">
            <FaVideo className="opt-icon" />
          </div>
          <div className="opt-text">
            <div className="opt-label">Video call</div>
            <div className="opt-sub">Zoom/online meeting</div>
          </div>
        </div>
        <div className="opt-right">
          {appointmentType === "video" ? <FaCheckCircle className="opt-check" /> : <span className="opt-circle" />}
        </div>
      </label>

      <label className={`opt-card ${appointmentType === "phone" ? "active" : ""}`}>
        <input
          type="radio"
          name="appointment-type"
          value="phone"
          checked={appointmentType === "phone"}
          onChange={() => setAppointmentType("phone")}
        />
        <div className="opt-left">
          <div className="opt-icon-wrap">
            <FaPhoneAlt className="opt-icon" />
          </div>
          <div className="opt-text">
            <div className="opt-label">Phone call</div>
            <div className="opt-sub">We’ll call your number</div>
          </div>
        </div>
        <div className="opt-right">
          {appointmentType === "phone" ? <FaCheckCircle className="opt-check" /> : <span className="opt-circle" />}
        </div>
      </label>
    </div>
  );

  const LeadCreation = () => (
    <div className="d-flex flex-column  gap-2 align-items-center">
      {createLeadSteps === 1 && (
        <div className="scheduler" style={{ alignItems: "stretch" }}>
          <div className="left-col bg-white rounded-4 p-3">
            <MonthGrid
              monthCursor={monthCursor}
              selectedDate={selectedDate}
              onSelectDate={pickDate}
              onPrevMonth={prevMonth}
              onNextMonth={nextMonth}
              isDayAvailable={isDayAvailable}
            />

            <div className="tz-row">
              <label className="tz-label">Time zone</label>
              <Select
                options={tzOptions}
                // value object uses friendly label + current time (like old form)
                value={{
                  value: selectedTZ.value,
                  label: selectedLabel(selectedTZ.value),
                }}
                onChange={(opt) => setSelectedTZ(opt)}
                classNamePrefix="tz"
                menuPlacement="auto"
              />
            </div>

            <div style={{ marginTop: 12 }}>
              {status === "success" && (
                <p style={{ color: "green" }}>{message}</p>
              )}
              {status === "error" && (
                <p style={{ color: "crimson" }}>{message}</p>
              )}
            </div>
          </div>

          <div className="right-col">
            <div className="card" style={{ height: "100%" }}>
              <div
                className="card-body"
                style={{ display: "flex", flexDirection: "column" }}
              >
                <div className="date-row">
                  <div className="date-title">
                    {format(selectedDate, "EEEE, MMM d")}
                  </div>
                  <div className="host-note">
                    {taxProAvailability?.firstname} •{" "}
                    {taxProAvailability?.settings?.timezone}
                  </div>
                </div>

                {slots.length ? (
                  <div
                    className="times"
                    style={{ maxHeight: 360, overflowY: "auto" }}
                  >
                    {slots.map((s) => (
                      <button
                        type="button"
                        key={s.iso}
                        className={[
                          "time-btn",
                          selectedSlotIso === s.iso ? "time-btn--active" : "",
                        ]
                          .join(" ")
                          .trim()}
                        onClick={() => setSelectedSlotIso(s.iso)}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="muted">
                    No times for this day. Pick another date.
                  </p>
                )}

                <AppointmentTypePicker
                  appointmentType={appointmentType}
                  setAppointmentType={setAppointmentType}
                />

                <div className="actions d-flex flex-column">
                  <button
                    type="button"
                    className="primary-btn w-100 rounded-pill"
                    disabled={!selectedSlotIso || loading}
                    onClick={() => setCreateLeadSteps(2)}
                  >
                    {loading && (
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                    )}{" "}
                    Next
                  </button>
                  {!selectedSlotIso && (
                    <span className="muted">Pick a time to continue</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {createLeadSteps === 2 && (
        <div>
          <div style={{ marginTop: 12 }}>
            {status === "success" && (
              <p style={{ color: "green" }}>{message}</p>
            )}
            {status === "error" && (
              <p style={{ color: "crimson" }}>{message}</p>
            )}
          </div>

          <div className="d-flex  justify-content-center gap-5 align-items-start">
            <div>
              <h4 className={`mb-3 text-dark text-start`}>
                What can we help you with?
              </h4>

              {servicesLoading && (
                <div className="text-muted mb-2">Loading services…</div>
              )}
              {!servicesLoading && services.length === 0 && (
                <div className="text-muted mb-2">No services found.</div>
              )}

              <div className="d-flex flex-column gap-2">
                {services?.map((s) => {
                  const checked = formData.options.includes(String(s.id));
                  return (
                    <label key={s.id} className={`text-dark ukf-pointer`}>
                      <input
                        type="checkbox"
                        className="form-check-input me-2"
                        checked={checked}
                        onChange={(e) => {
                          const id = String(s.id);
                          setFormData((prev) => {
                            const next = new Set(prev.options.map(String));
                            if (e.target.checked) next.add(id);
                            else next.delete(id);
                            return { ...prev, options: Array.from(next) };
                          });
                        }}
                      />
                      {s.name}
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="d-flex flex-column gap-2">
                <h4 className={`mb-3 text-start text-dark`}>
                  What is your annual income?
                </h4>
                {ANNUAL_INCOMES.map((inc) => (
                  <label key={inc.id} className={`text-dark ukf-pointer`}>
                    <input
                      type="radio"
                      name="annual_income"
                      className="form-check-input me-2"
                      checked={String(formData.income) === String(inc.id)}
                      value={inc.id}
                      onChange={(e) =>
                        setFormData({ ...formData, income: e.target.value })
                      }
                    />
                    {inc.name}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label htmlFor="zip" className="form-label text-black">
              Zip Code
            </label>
            <input
              id="zip"
              name="zip"
              className="form-control w-50"
              placeholder="Enter your Zip Code here"
              inputMode="numeric"
              autoComplete="postal-code"
              maxLength={8}
              defaultValue={zipRef.current}
              onInput={(e) => {
                const el = e.currentTarget;
                const filtered = el.value.replace(/\D/g, "").slice(0, 8);
                if (filtered !== el.value) {
                  const pos = el.selectionStart || filtered.length;
                  el.value = filtered;
                  requestAnimationFrame(() => el.setSelectionRange(pos, pos));
                }
                zipRef.current = filtered;
              }}
            />
          </div>

          <div className="mt-3">
            <button
              className="btn btn-primary btn-lg"
              onClick={async () => {
                setLoading(true);
                try {
                  const zip = zipRef.current?.value || "";
                  const res = await axios.post(
                    PROXY_URL,
                    {
                      path: "/create-lead-by-email",
                      method: "POST",
                      qa_mode: qa,
                      data: {
                        email,
                        services: formData.options,
                        income: formData.income,
                        pro_id: taxProAvailability?.pro_id || null,
                        zip,
                      },
                    },
                    { headers: { "Content-Type": "application/json" } }
                  );

                  if (res?.data?.success) {
                    await createAppointment(
                      {
                        lead: res.data.data,
                        user: res.data.data?.user,
                        taxPro: res.data.data?.userpro,
                      },
                      false
                    );
                  }
                } catch (err) {
                  console.log(err);
                } finally {
                  setLoading(false);
                }
              }}
              disabled={formData?.options?.length === 0 || !formData?.income}
            >
              {loading && (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
              )}{" "}
              Submit
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const ready = userCheck && Object.keys(userCheck).length > 0;

  return (
    <>
      {!ready && (
        <div className="scheduler">
          {status === "error" && (
            <p style={{ color: "crimson" }}>Error: {message}</p>
          )}
        </div>
      )}

      {ready && userCheck?.userExist && !userCheck?.leadExists && (
        <LeadCreation />
      )}

      {ready && !userCheck?.userExist && (
        <div>
          {createUserSteps === 1 && (
            <div className="scheduler" style={{ alignItems: "stretch" }}>
              <div className="left-col bg-white p-3 rounded-4">
                <MonthGrid
                  monthCursor={monthCursor}
                  selectedDate={selectedDate}
                  onSelectDate={pickDate}
                  onPrevMonth={prevMonth}
                  onNextMonth={nextMonth}
                  isDayAvailable={isDayAvailable}
                />

                <div className="tz-row">
                  <label className="tz-label">Time zone</label>
                  <Select
                    options={tzOptions}
                    value={{ value: selectedTZ.value, label: selectedLabel(selectedTZ.value) }}
                    onChange={(opt) => setSelectedTZ(opt)}
                    classNamePrefix="tz"
                    menuPlacement="auto"
                  />
                </div>

                <div style={{ marginTop: 12 }}>
                  {status === "success" && (
                    <p style={{ color: "green" }}>{message}</p>
                  )}
                  {status === "error" && (
                    <p style={{ color: "crimson" }}>{message}</p>
                  )}
                </div>
              </div>

              <div className="right-col">
                <div className="card" style={{ height: "100%" }}>
                  <div
                    className="card-body"
                    style={{ display: "flex", flexDirection: "column" }}
                  >
                    <div className="date-row">
                      <div className="date-title">
                        {format(selectedDate, "EEEE, MMM d")}
                      </div>
                      <div className="host-note">
                        {taxProAvailability?.firstname} •{" "}
                        {taxProAvailability?.settings?.timezone}
                      </div>
                    </div>

                    {slots.length ? (
                      <div
                        className="times"
                        style={{ maxHeight: 360, overflowY: "auto" }}
                      >
                        {slots.map((s) => (
                          <button
                            type="button"
                            key={s.iso}
                            className={[
                              "time-btn",
                              selectedSlotIso === s.iso
                                ? "time-btn--active"
                                : "",
                            ]
                              .join(" ")
                              .trim()}
                            onClick={() => setSelectedSlotIso(s.iso)}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="muted">
                        No times for this day. Pick another date.
                      </p>
                    )}

                    <AppointmentTypePicker
                      appointmentType={appointmentType}
                      setAppointmentType={setAppointmentType}
                    />

                    <div className="actions d-flex flex-column">
                      <button
                        type="button"
                        className="primary-btn w-100 rounded-pill"
                        disabled={!selectedSlotIso || loading}
                        onClick={() => setCreateUserSteps(2)}
                      >
                        {loading && (
                          <span
                            className="spinner-border spinner-border-sm"
                            role="status"
                            aria-hidden="true"
                          ></span>
                        )}{" "}
                        Next
                      </button>
                      {!selectedSlotIso && (
                        <span className="muted">Pick a time to continue</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {createUserSteps === 2 && (
            <MultiStepLeadForm
              email={email}
              qa_mode={qa}
              clientUrl={CLIENT_URL}
              auto_login={auto_login}
              fromBooking={true}
              createAppointment={createAppointment}
              pro_id={taxProAvailability?.pro_uuid}
               proxyUrl={PROXY_URL}
            />
          )}
        </div>
      )}

      {ready && userCheck?.leadExists && userCheck?.taxProAssigned && (
        <div className="scheduler" style={{ alignItems: "stretch" }}>
          <div className="left-col bg-white rounded-4 p-3">
            <MonthGrid
              monthCursor={monthCursor}
              selectedDate={selectedDate}
              onSelectDate={pickDate}
              onPrevMonth={prevMonth}
              onNextMonth={nextMonth}
              isDayAvailable={isDayAvailable}
            />

            <div className="tz-row">
              <label className="tz-label">Time zone</label>
              <Select
                options={tzOptions}
                value={{ value: selectedTZ.value, label: selectedLabel(selectedTZ.value) }}
                onChange={(opt) => setSelectedTZ(opt)}
                classNamePrefix="tz"
                menuPlacement="auto"
              />
            </div>

            <div style={{ marginTop: 12 }}>
              {status === "success" && (
                <p style={{ color: "green" }}>{message}</p>
              )}
              {status === "error" && (
                <p style={{ color: "crimson" }}>{message}</p>
              )}
            </div>
          </div>

          <div className="right-col">
            <div className="card" style={{ height: "100%" }}>
              <div
                className="card-body"
                style={{ display: "flex", flexDirection: "column" }}
              >
                <div className="date-row">
                  <div className="date-title">
                    {format(selectedDate, "EEEE, MMM d")}
                  </div>
                  <div className="host-note">
                    {taxProAvailability?.firstname} •{" "}
                    {taxProAvailability?.settings?.timezone}
                  </div>
                </div>

                {slots.length ? (
                  <div
                    className="times"
                    style={{ maxHeight: 360, overflowY: "auto" }}
                  >
                    {slots.map((s) => (
                      <button
                        type="button"
                        key={s.iso}
                        className={[
                          "time-btn",
                          selectedSlotIso === s.iso ? "time-btn--active" : "",
                        ]
                          .join(" ")
                          .trim()}
                        onClick={() => setSelectedSlotIso(s.iso)}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="muted">
                    No times for this day. Pick another date.
                  </p>
                )}

                <AppointmentTypePicker
                  appointmentType={appointmentType}
                  setAppointmentType={setAppointmentType}
                />

                <div className="actions d-flex flex-column">
                  <button
                    type="button"
                    className="primary-btn w-100 rounded-pill"
                    disabled={!selectedSlotIso || loading}
                    onClick={() => createAppointment(lead,false)}
                  >
                    {loading && (
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                    )}{" "}
                    Book Appointment
                  </button>
                  {!selectedSlotIso && (
                    <span className="muted">Pick a time to continue</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BookAppointmentFrom;
